import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
} from '@design-system';

import { Send } from 'lucide-react';

import { useSignatoryEmails } from '../hooks/use-signatory-emails';

interface SignatoryEmailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string | null | undefined;
  isSending: boolean;
  onSend: (roleEmails: Record<string, string>) => void;
}

export function SignatoryEmailsDialog({
  open,
  onOpenChange,
  formId,
  isSending,
  onSend,
}: SignatoryEmailsDialogProps) {
  const {
    rows,
    advisorEmail,
    setEmail,
    errors,
    isValid,
    getRoleEmails,
    isLoading,
    error,
    unmappedRoleNames,
  } = useSignatoryEmails({ open, formId });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>Send Test Envelope to DocuSign</DialogTitle>
          <DialogDescription>
            Assign a test email to each signatory required by this template. The application
            preparer always uses your account email. Other roles are pre-filled with tagged variants
            of your email — edit them to test the full signing flow with real inboxes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-auto py-2">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-5" />
              <span className="text-muted-foreground ml-2 text-sm">
                Loading signatories from DocuSign…
              </span>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load template signatories. Make sure this form has a DocuSign template
                configured.
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !error && rows.length === 0 && (
            <Alert>
              <AlertDescription>
                This template has no signer roles with signing tabs, so there is nothing to send.
                Add signing tabs to the template in DocuSign first.
              </AlertDescription>
            </Alert>
          )}

          {!isLoading &&
            !error &&
            rows.map(row => {
              const isPreparer = row.roleKey === 'applicationPreparer';
              return (
                <div
                  key={row.roleKey}
                  className="flex flex-col gap-1.5"
                >
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`signatory-${row.roleKey}`}
                      className="text-sm font-medium"
                    >
                      {row.label}
                    </Label>
                    {isPreparer && (
                      <Badge
                        variant="secondary"
                        className="text-[10px]"
                      >
                        Your account
                      </Badge>
                    )}
                  </div>
                  <Input
                    id={`signatory-${row.roleKey}`}
                    type="email"
                    value={isPreparer ? advisorEmail : row.email}
                    disabled={isPreparer}
                    onChange={e => setEmail(row.roleKey, e.target.value)}
                    placeholder="name@example.com"
                    aria-invalid={Boolean(errors[row.roleKey])}
                  />
                  {errors[row.roleKey] && (
                    <p className="text-destructive text-xs">{errors[row.roleKey]}</p>
                  )}
                </div>
              );
            })}

          {!isLoading && !error && unmappedRoleNames.length > 0 && (
            <Alert>
              <AlertDescription className="text-xs">
                The template has signer role(s) we can't map to a known signatory and won't include:{' '}
                {unmappedRoleNames.join(', ')}.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSend(getRoleEmails())}
            disabled={isSending || isLoading || Boolean(error) || !isValid}
          >
            {isSending ? <Spinner className="size-4" /> : <Send />}
            Send to DocuSign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
